import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import MatchMarket from './MatchMarket';
import './MatchPrediction.css';

/**
 * Beräkna poängen ett tips ger vid rätt svar enligt den odds-viktade
 * modellen (speglar backendens `calculate_points`: 100 × (1 − odds),
 * golvat till 5).
 *
 * @param {number} odds Förväntad vinstchans (0–1) för det valda laget.
 * @returns {number} Poäng vid korrekt tips.
 */
export function potentialPoints(odds) {
  return Math.max(5, Math.round(100 * (1 - odds)));
}

/**
 * MatchPrediction — vinnartips-kortet för en match.
 *
 * Före GOING_LIVE: två lag-knappar med odds och möjlig poäng; inloggade
 * aktiva spelare kan lägga/ändra sitt tips. Efter låset visas community-
 * fördelningen (alltid vid lås; innan lås bara om PREDICTIONS_VISIBILITY är
 * "open" — backend avgör och utelämnar annars fältet). När matchen är
 * avgjord visas facit och poängen för det egna tipset.
 *
 * @param {{matchId: number|string, compact?: boolean}} props Match-id och
 *   valfri kompakt variant (används i live-hubben).
 * @returns {JSX.Element|null} Kortet, eller null när matchen saknar lag/odds.
 */
const MatchPrediction = ({ matchId, compact = false }) => {
  const { t } = useTranslation();
  const { isLoggedIn, getAuthHeaders } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  /**
   * Hämta tips-läget för matchen (odds, lås, fördelning, eget tips).
   * JWT:n skickas med när användaren är inloggad så `my_prediction` fylls i.
   */
  const fetchState = useCallback(async () => {
    try {
      const headers = isLoggedIn ? getAuthHeaders() : {};
      const r = await fetch(`${API_URL}/api/predictions/by-match/${matchId}`, { headers });
      if (r.ok) {
        setState(await r.json());
      }
    } catch {
      // tyst — tipskortet är icke-kritiskt för matchsidan
    } finally {
      setLoading(false);
    }
  }, [matchId, isLoggedIn, getAuthHeaders]);

  useEffect(() => {
    fetchState();
  }, [fetchState]);

  /**
   * Lägg eller ändra det egna tipset och hämta sedan om läget.
   *
   * @param {"team1"|"team2"} team Laget tipset läggs på.
   */
  const predict = async (team) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/predictions/predict`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ match_id: Number(matchId), picked_team: team }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        setError(data.code === 'own_team_only'
          ? t('predictions.errOwnTeam')
          : (data.error || data.detail || t('predictions.error')));
      } else {
        await fetchState();
      }
    } catch {
      setError(t('predictions.error'));
    } finally {
      setBusy(false);
    }
  };

  if (loading || !state || !state.odds) return null;

  const {
    odds, locked, distribution, total_predictions: total, winner,
    own_side: ownSide,
  } = state;
  const myPick = state.my_prediction?.picked_team || null;
  const scored = state.my_prediction?.points_awarded !== null
    && state.my_prediction?.points_awarded !== undefined;
  const finished = !!winner;
  const canPick = !locked && isLoggedIn;
  const extraMarkets = (state.offered_markets || []).filter((m) => m !== 'winner');

  const teamName = (key) =>
    key === 'team1' ? (state.team1_name || 'Team 1') : (state.team2_name || 'Team 2');

  /**
   * Skicka ett tips på en valfri marknad och hämta om läget.
   *
   * @param {string} market Marknad (top_fragger, pistol_round, …).
   * @param {string} pick Pick-värde (team-nyckel, steamid64 eller resultat).
   */
  const predictMarket = async (market, pick) => {
    if (busy || !pick) return;
    setBusy(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/predictions/predict`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ match_id: Number(matchId), market, pick }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        setError(data.error || data.detail || t('predictions.error'));
      } else {
        await fetchState();
      }
    } catch {
      setError(t('predictions.error'));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Facit/status-etikett för ett eget market-tips.
   *
   * @param {object} mine my_prediction-objektet för marknaden.
   * @returns {JSX.Element|null} Status-span, eller null utan tips.
   */
  const renderMarketStatus = (mine) => {
    if (!mine) return null;
    if (mine.points_awarded === null || mine.points_awarded === undefined) {
      return <span className="market-status pending">{mine.pick_label}</span>;
    }
    if (mine.is_correct === null) {
      return <span className="market-status voided">— {t('predictions.voided')}</span>;
    }
    if (mine.is_correct) {
      return (
        <span className="market-status correct">
          ✅ +{mine.points_awarded} {t('predictions.points')}
        </span>
      );
    }
    return (
      <span className="market-status wrong">
        ❌ {mine.points_awarded < 0 ? mine.points_awarded : 0} {t('predictions.points')}
      </span>
    );
  };

  /**
   * Rendera en extra-marknadsrad: etikett + kontroll (eller facit efter lås).
   *
   * @param {string} market Marknadsnyckel.
   * @returns {JSX.Element} Marknadsraden.
   */
  const renderMarket = (market) => {
    const entry = state.markets?.[market] || {};
    const mine = entry.my_prediction;
    const labelKey = {
      top_fragger: 'predictions.marketTopFragger',
      top_adr: 'predictions.marketTopAdr',
      series_score: 'predictions.marketSeriesScore',
      pistol_round: 'predictions.marketPistol',
    }[market] || market;

    let control = null;
    if (canPick) {
      if (market === 'pistol_round') {
        control = (
          <div className="market-buttons">
            {['team1', 'team2'].map((key) => (
              <button
                key={key}
                type="button"
                className={`market-btn ${mine?.pick === key ? 'mine' : ''}`}
                disabled={busy}
                onClick={() => predictMarket(market, key)}
              >
                {teamName(key)}
              </button>
            ))}
          </div>
        );
      } else if (market === 'series_score') {
        control = (
          <div className="market-buttons">
            {(entry.options || []).map((option) => (
              <button
                key={option}
                type="button"
                className={`market-btn ${mine?.pick === option ? 'mine' : ''}`}
                disabled={busy}
                onClick={() => predictMarket(market, option)}
              >
                {option}
              </button>
            ))}
          </div>
        );
      } else {
        control = (
          <select
            className="market-select"
            value={mine?.pick || ''}
            disabled={busy}
            onChange={(e) => predictMarket(market, e.target.value)}
          >
            <option value="" disabled>{t('predictions.choosePlayer')}</option>
            {(state.player_options || []).map((option) => (
              <option key={option.steamid64} value={option.steamid64}>
                {option.name} ({teamName(option.team)})
              </option>
            ))}
          </select>
        );
      }
    }

    return (
      <div key={market} className="market-row">
        <span className="market-label">{t(labelKey)}</span>
        {control}
        {(locked || mine) && renderMarketStatus(mine)}
        {!mine && locked && !finished && (
          <span className="market-status pending">🔒</span>
        )}
        {finished && entry.winner_labels && (
          <span className="market-facit">
            → {entry.winner_labels.join(', ')}
          </span>
        )}
      </div>
    );
  };

  /**
   * Rendera en lag-knapp/ruta med odds, poängpotential och pick-markering.
   *
   * @param {"team1"|"team2"} key Lag-nyckel.
   * @returns {JSX.Element} Lagrutan.
   */
  const renderTeam = (key) => {
    const isMine = myPick === key;
    const isWinner = winner === key;
    // Spelar man i matchen är motståndarsidan spärrad (own_team_only) — visa
    // det som en låst knapp i stället för att låta klicket alltid nekas.
    const blocked = !!ownSide && ownSide !== key;
    const pickable = canPick && !blocked;
    const classes = [
      'prediction-team',
      isMine ? 'mine' : '',
      finished && isWinner ? 'winner' : '',
      finished && isMine && !isWinner ? 'loser-pick' : '',
      pickable ? 'clickable' : '',
      blocked && !finished ? 'blocked' : '',
    ].filter(Boolean).join(' ');
    return (
      <button
        type="button"
        className={classes}
        disabled={!pickable || busy}
        onClick={() => pickable && predict(key)}
        title={blocked && !finished ? t('predictions.errOwnTeam') : undefined}
      >
        <span className="prediction-team-name">
          {finished && isWinner && <span className="prediction-trophy">🏆</span>}
          {teamName(key)}
          {isMine && <span className="prediction-my-badge">{t('predictions.yourPick')}</span>}
        </span>
        <span className="prediction-team-odds">{Math.round(odds[key] * 100)}%</span>
        {!locked && (
          <span className="prediction-team-points">
            +{potentialPoints(odds[key])} {t('predictions.points')}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className={`match-prediction ${compact ? 'compact' : ''}`}>
      <div className="match-prediction-header">
        <span className="match-prediction-title">🔮 {t('predictions.title')}</span>
        {locked && !finished && (
          <span className="match-prediction-locked">🔒 {t('predictions.lockedLabel')}</span>
        )}
        {total > 0 && (
          <span className="match-prediction-total">
            {total} {t('predictions.votes')}
          </span>
        )}
      </div>

      {!locked && !isLoggedIn && (
        <div className="match-prediction-hint">{t('predictions.loginToPredict')}</div>
      )}
      {!locked && isLoggedIn && !myPick && (
        <div className="match-prediction-hint">{t('predictions.pickPrompt')}</div>
      )}

      <div className="prediction-teams">
        {renderTeam('team1')}
        <span className="prediction-vs">vs</span>
        {renderTeam('team2')}
      </div>

      {distribution && total > 0 && (
        <div className="prediction-distribution">
          <div className="prediction-bar">
            <div
              className="prediction-bar-team1"
              style={{ width: `${(distribution.team1 / total) * 100}%` }}
            />
          </div>
          <div className="prediction-bar-labels">
            <span>{distribution.team1} · {teamName('team1')}</span>
            <span>{teamName('team2')} · {distribution.team2}</span>
          </div>
        </div>
      )}
      {!distribution && !locked && total > 0 && (
        <div className="match-prediction-hint subtle">{t('predictions.hiddenUntilLock')}</div>
      )}

      {finished && myPick && scored && (
        <div
          className={`prediction-result ${state.my_prediction.is_correct ? 'correct' : 'wrong'}`}
        >
          {state.my_prediction.is_correct
            ? `✅ ${t('predictions.correct')} +${state.my_prediction.points_awarded} ${t('predictions.points')}`
            : `❌ ${t('predictions.wrong')} ${state.my_prediction.points_awarded} ${t('predictions.points')}`}
        </div>
      )}

      {extraMarkets.length > 0 && (canPick || Object.values(state.markets || {}).some((m) => m.my_prediction)) && (
        <div className="prediction-markets">
          <div className="prediction-markets-title">{t('predictions.marketsTitle')}</div>
          {extraMarkets.map(renderMarket)}
        </div>
      )}

      <MatchMarket matchId={matchId} />

      {error && <div className="match-prediction-error">{error}</div>}
    </div>
  );
};

export default MatchPrediction;
